import React, { ChangeEvent } from 'react';

import { StaffTemplate } from '../../shared/types/staffTemplate';

import { BrandStaffDepartment } from '../../shared/types/brand';
import { PropertyBerthing, Property, PropertyType } from '../../shared/types/property';
import { FormGroup, Typeahead, Input, Button } from '@spark-labs/avocado-ui';
import { InputElement } from '@spark-labs/avocado-ui/dist/components/Input';
import { TypeaheadItem } from '@spark-labs/avocado-ui/dist/components/Typeahead';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { toast } from './UIkit/Toast/Toast';

/**
 * This is similar to the `StaffTemplate` type, however it allows nullable department and berthing
 * since they may have not been assigned yet
 *
 */
export interface EditedStaffTemplate {
  _id: string;
  title: string;
  department?: BrandStaffDepartment;
  berthing?: PropertyBerthing;
  quantity: number;
  property: Property;
}

export interface StaffTemplateFormProps {
  staffTemplate: EditedStaffTemplate;
  propertyType: PropertyType;
  showNewStaffBtn: boolean;
  onSubmit?: (staffTemplate: StaffTemplate) => void;
}

export interface StaffTemplateFormState {
  editedStaffTemplate: EditedStaffTemplate;
}

export default class StaffTemplateForm extends React.Component<
  StaffTemplateFormProps,
  StaffTemplateFormState
> {
  public readonly state: StaffTemplateFormState = { editedStaffTemplate: this.props.staffTemplate };

  constructor(props: StaffTemplateFormProps) {
    super(props);

    this.handleInputChange = this.handleInputChange.bind(this);

    this.handleDepartmentChange = this.handleDepartmentChange.bind(this);
    this.handleBerthingChange = this.handleBerthingChange.bind(this);

    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  public render() {
    let staffTemplate = this.state.editedStaffTemplate;

    let departments = staffTemplate.property.brand.staffDepartments.map(dept => {
      return { key: dept._id, element: dept.name };
    });
    let berthings = staffTemplate.property.staffBerthings.map(berthing => {
      return { key: berthing._id, element: berthing.name };
    });

    let berthingLabel = getPropertyConfiguration(this.props.propertyType, 'berthingLabel');

    return (
      <div className="border-t border-grey pt-2 p-4 mt-6" style={{ width: '400px' }}>
        <fieldset disabled={this.isLocked()}>
          <div className="flex">
            <div className="w-full p-4">
              <FormGroup>
                <Typeahead
                  label="Department"
                  value={staffTemplate.department ? staffTemplate.department.name : undefined}
                  items={departments}
                  onSelect={this.handleDepartmentChange}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  name="title"
                  label="Title"
                  onChange={this.handleInputChange}
                  value={staffTemplate.title}
                />
              </FormGroup>
              <FormGroup>
                <Typeahead
                  label={berthingLabel}
                  value={staffTemplate.berthing ? staffTemplate.berthing.name : undefined}
                  items={berthings}
                  onSelect={this.handleBerthingChange}
                />
              </FormGroup>
              <FormGroup>
                <Input
                  name="quantity"
                  label="Quantity"
                  onChange={this.handleInputChange}
                  value={staffTemplate.quantity}
                />
              </FormGroup>
            </div>
          </div>
          {!this.isLocked() && (
            <div className="flex justify-end">
              <div className="mx-2">
                <Button color="green" onClick={this.handleFormSubmit}>
                  Submit
                </Button>
              </div>
            </div>
          )}
        </fieldset>
      </div>
    );
  }

  private isLocked = () => !this.props.showNewStaffBtn;

  private async handleFormSubmit() {
    // Validate and convert the nullable `EditedStaffTemplate` object into a proper
    // `StaffTemplate` one
    if (this.isLocked()) {
      await toast.warning('Staff template editing is locked');
      return;
    }

    try {
      if (!this.state.editedStaffTemplate.department) {
        throw new Error(`Must select a valid department`);
      }

      if (!this.state.editedStaffTemplate.berthing) {
        throw new Error(`Must select a valid berthing`);
      }

      if (this.props.onSubmit) {
        this.props.onSubmit(this.state.editedStaffTemplate as StaffTemplate);
      }
    } catch (e) {
      if (e instanceof Error && e.message) {
        await toast.error(e.message);
      }
    }
  }

  private handleInputChange(event: ChangeEvent<InputElement>) {
    let newState = { ...this.state.editedStaffTemplate, [event.target.name]: event.target.value };
    this.setState({ editedStaffTemplate: newState as any });
  }

  private handleDepartmentChange(item: TypeaheadItem) {
    let department = {
      _id: item.key,
      name: item.element,
    };

    this.setState({ editedStaffTemplate: { ...this.state.editedStaffTemplate, department } });
  }

  private handleBerthingChange(item: TypeaheadItem) {
    let berthing = {
      _id: item.key,
      name: item.element,
    };

    this.setState({ editedStaffTemplate: { ...this.state.editedStaffTemplate, berthing } });
  }
}
